import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  X,
  Loader2,
  Snowflake,
  Sparkles,
  Send,
  Ban,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

const EXCLUSION_LABELS = {
  no_phone: "No phone number on record",
  do_not_message: "On do-not-message list (opted out)",
  sent_within_14d: "Already messaged in the last 14 days",
};

function renderRendered(template, params) {
  if (!template) return "";
  return template.replace(/\{\{(\d+)\}\}/g, (_m, n) => {
    const idx = parseInt(n, 10) - 1;
    return params?.[idx] ?? `{{${n}}}`;
  });
}

export default function ReactivationDialog({ open, onClose, onBatchStarted }) {
  const [step, setStep] = useState("loading"); // loading | review | starting | running | done | error
  const [preview, setPreview] = useState(null);
  const [connections, setConnections] = useState([]);
  const [connId, setConnId] = useState("");
  const [selectedConn, setSelectedConn] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [params, setParams] = useState([]);
  const [goal, setGoal] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [batch, setBatch] = useState(null);
  const [showExcluded, setShowExcluded] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const pollRef = useRef(null);

  const loadPreview = useCallback(async (chosenConnId) => {
    setStep("loading");
    setErrMsg("");
    try {
      const r = await api.post("/reactivation/preview", {
        aisensy_connection_id: chosenConnId || null,
      });
      setPreview(r.data);
      const conns = r.data?.aisensy_connections || [];
      setConnections(conns);
      const chosen = r.data?.selected_connection || conns[0] || null;
      setSelectedConn(chosen);
      setConnId(chosen?.id || "");
      // Prefill params from an empty array of correct length
      const pc = parseInt(chosen?.param_count || 0, 10);
      setParams(Array.from({ length: pc }, () => ""));
      // Preselect all eligible
      setSelectedIds((r.data?.eligible || []).map((c) => c.ghl_contact_id));
      setStep("review");
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || "Could not load cold-lead preview.");
      setStep("error");
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setBatch(null);
    setShowExcluded(false);
    loadPreview();
  }, [open, loadPreview]);

  // Switch connection → reload preview with connection id + reset params.
  const onConnChange = async (id) => {
    setConnId(id);
    const c = connections.find((x) => x.id === id) || null;
    setSelectedConn(c);
    const pc = parseInt(c?.param_count || 0, 10);
    setParams(Array.from({ length: pc }, () => ""));
  };

  const draftParams = async () => {
    if (!connId) {
      toast.error("Pick an AiSensy account first.");
      return;
    }
    setDrafting(true);
    try {
      const r = await api.post("/reactivation/draft-params", {
        aisensy_connection_id: connId,
        goal: goal || "",
      });
      setParams(r.data?.params || []);
      toast.success("Draft ready. Tweak it if you like.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed.");
    } finally {
      setDrafting(false);
    }
  };

  const startBatch = async () => {
    if (!connId) {
      toast.error("Pick an AiSensy account first.");
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Select at least one contact.");
      return;
    }
    const pc = parseInt(selectedConn?.param_count || 0, 10);
    if (params.length !== pc || params.some((p) => !String(p || "").trim())) {
      toast.error("Fill every template slot before starting.");
      return;
    }
    setStarting(true);
    try {
      const r = await api.post("/reactivation/start", {
        aisensy_connection_id: connId,
        contact_ids: selectedIds,
        params,
      });
      // Load the batch doc
      const bres = await api.get(`/reactivation/batches/${r.data.batch_id}`);
      setBatch(bres.data);
      setStep("running");
      if (onBatchStarted) onBatchStarted(r.data.batch_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start batch.");
    } finally {
      setStarting(false);
    }
  };

  const cancelBatch = async () => {
    if (!batch?.id) return;
    try {
      await api.post(`/reactivation/batches/${batch.id}/cancel`);
      toast.info("Cancel requested. In-flight sends will complete.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed.");
    }
  };

  // Progress poll while batch is running
  useEffect(() => {
    if (step !== "running" || !batch?.id) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/reactivation/batches/${batch.id}`);
        setBatch(r.data);
        if (["done", "error", "cancelled"].includes(r.data.status)) {
          setStep("done");
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        // silent — will retry
      }
    }, 2000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [step, batch?.id]);

  const toggleId = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const toggleAll = () => {
    const all = (preview?.eligible || []).map((c) => c.ghl_contact_id);
    if (selectedIds.length === all.length) setSelectedIds([]);
    else setSelectedIds(all);
  };

  const renderedPreview = useMemo(
    () => renderRendered(selectedConn?.template_text, params),
    [selectedConn?.template_text, params],
  );

  if (!open) return null;

  const eligible = preview?.eligible || [];
  const excluded = preview?.excluded || [];

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      data-testid="reactivation-dialog-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && step !== "running") onClose();
      }}
    >
      <div
        className="w-full max-w-4xl max-h-[92vh] overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl flex flex-col"
        data-testid="reactivation-dialog"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-900">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center">
              <Snowflake className="w-5 h-5 text-sky-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-emerald-400">
                // AiSensy Campaign
              </div>
              <h2 className="font-display text-2xl font-black text-white tracking-tight">
                Reactivate Cold Leads
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={step === "running"}
            className="p-2 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-zinc-900 disabled:opacity-40"
            data-testid="reactivation-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {step === "loading" && (
            <div className="py-12 text-center text-sm text-zinc-500 inline-flex items-center gap-2 justify-center w-full">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading cold-lead pool…
            </div>
          )}

          {step === "error" && (
            <div className="p-4 rounded-lg border border-rose-500/30 bg-rose-500/5 text-rose-300 text-sm">
              <AlertTriangle className="w-4 h-4 inline mr-1.5" />
              {errMsg}
            </div>
          )}

          {step === "review" && preview && (
            <div className="space-y-5">
              {/* Summary line */}
              <div
                data-testid="reactivation-summary"
                className="rounded-xl border border-zinc-900 bg-zinc-900/40 px-4 py-3 text-sm text-zinc-300 flex flex-wrap items-center gap-x-4 gap-y-1"
              >
                <span>
                  <span className="text-emerald-300 font-display font-black text-lg">
                    {eligible.length}
                  </span>{" "}
                  cold leads eligible
                </span>
                <span className="text-zinc-600">·</span>
                <span>
                  <span className="text-zinc-400 font-display font-black text-lg">
                    {excluded.length}
                  </span>{" "}
                  auto-excluded
                </span>
                {preview.eligible_total > eligible.length && (
                  <>
                    <span className="text-zinc-600">·</span>
                    <span className="text-amber-300 text-xs">
                      Capped at {preview.cap} — {preview.eligible_total - eligible.length} more waiting
                    </span>
                  </>
                )}
              </div>

              {/* Connection picker */}
              {connections.length === 0 ? (
                <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 text-amber-200 text-sm">
                  No AiSensy account is connected. Ask your programme team to
                  add one before running a campaign.
                </div>
              ) : (
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    AiSensy account
                  </label>
                  <select
                    data-testid="reactivation-conn-select"
                    value={connId}
                    onChange={(e) => onConnChange(e.target.value)}
                    className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/40"
                  >
                    {connections.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label || "(unnamed)"} · {c.campaign_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {selectedConn && (
                <div className="rounded-xl border border-zinc-900 bg-zinc-900/30 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                      Message template
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Goal / tone (optional, e.g. offer 10% off)"
                        value={goal}
                        onChange={(e) => setGoal(e.target.value)}
                        className="hidden md:block text-xs bg-zinc-950 border border-zinc-800 rounded-md px-2.5 py-1.5 text-zinc-300 w-72 focus:outline-none focus:border-emerald-500/40"
                        data-testid="reactivation-goal-input"
                      />
                      <button
                        onClick={draftParams}
                        disabled={drafting || !connId}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] px-2.5 py-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40"
                        data-testid="reactivation-draft-btn"
                      >
                        {drafting ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Sparkles className="w-3 h-3" />
                        )}
                        AI draft
                      </button>
                    </div>
                  </div>
                  <div className="rounded-lg bg-zinc-950 border border-zinc-900 p-3 text-sm text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">
                    {selectedConn.template_text}
                  </div>
                  {params.length > 0 && (
                    <div className="space-y-2">
                      {params.map((p, i) => (
                        <div key={i}>
                          <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                            {`{{${i + 1}}}`}
                          </label>
                          <input
                            data-testid={`reactivation-param-${i + 1}`}
                            value={p}
                            onChange={(e) => {
                              const copy = [...params];
                              copy[i] = e.target.value;
                              setParams(copy);
                            }}
                            className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/40"
                            placeholder="Type a value…"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {renderedPreview && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                        Preview
                      </div>
                      <div className="rounded-lg bg-emerald-950/20 border border-emerald-500/20 p-3 text-sm text-emerald-100 whitespace-pre-wrap">
                        {renderedPreview}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Eligible contacts table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    Recipients ({selectedIds.length} / {eligible.length} selected)
                  </div>
                  <button
                    onClick={toggleAll}
                    className="text-[11px] uppercase tracking-[0.18em] text-emerald-300 hover:text-emerald-200"
                    data-testid="reactivation-toggle-all"
                  >
                    {selectedIds.length === eligible.length ? "Clear all" : "Select all"}
                  </button>
                </div>
                <div className="rounded-lg border border-zinc-900 max-h-64 overflow-y-auto divide-y divide-zinc-900">
                  {eligible.length === 0 ? (
                    <div className="p-6 text-center text-sm text-zinc-500">
                      No eligible cold leads right now.
                    </div>
                  ) : (
                    eligible.map((c) => (
                      <label
                        key={c.ghl_contact_id}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-900/40 cursor-pointer"
                        data-testid={`reactivation-row-${c.ghl_contact_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(c.ghl_contact_id)}
                          onChange={() => toggleId(c.ghl_contact_id)}
                          className="w-4 h-4 accent-emerald-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate">{c.name}</div>
                          <div className="text-[11px] text-zinc-500 truncate">
                            {c.phone} {c.band ? `· ${c.band}${c.score != null ? ` · ${c.score}` : ""}` : "· unscored"}
                          </div>
                        </div>
                        <div className="text-[10px] text-zinc-600 shrink-0">
                          {c.days_since_activity != null
                            ? `${c.days_since_activity}d idle`
                            : "no activity"}
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Excluded (collapsible) */}
              {excluded.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowExcluded((v) => !v)}
                    className="w-full flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300"
                    data-testid="reactivation-excluded-toggle"
                  >
                    {showExcluded ? (
                      <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronRight className="w-3 h-3" />
                    )}
                    {excluded.length} auto-excluded contacts
                  </button>
                  {showExcluded && (
                    <div className="mt-2 rounded-lg border border-zinc-900 max-h-60 overflow-y-auto divide-y divide-zinc-900">
                      {excluded.map((c) => (
                        <div
                          key={c.ghl_contact_id + c.reason}
                          className="flex items-center justify-between px-3 py-2 text-xs"
                          data-testid={`reactivation-excluded-${c.ghl_contact_id}`}
                        >
                          <div className="text-zinc-400 truncate">{c.name}</div>
                          <div className="text-[10px] text-zinc-500 shrink-0 ml-3">
                            {EXCLUSION_LABELS[c.reason] || c.reason}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {(step === "running" || step === "done") && batch && (
            <div className="space-y-4">
              <div
                data-testid="reactivation-progress"
                className={`rounded-xl p-4 border ${
                  batch.status === "done"
                    ? "border-emerald-500/30 bg-emerald-500/5"
                    : batch.status === "cancelled"
                      ? "border-amber-500/30 bg-amber-500/5"
                      : batch.status === "error"
                        ? "border-rose-500/30 bg-rose-500/5"
                        : "border-sky-500/30 bg-sky-500/5"
                }`}
              >
                <div className="flex items-center gap-2 text-sm text-white">
                  {batch.status === "done" && <CheckCircle2 className="w-4 h-4 text-emerald-300" />}
                  {batch.status === "cancelled" && <Ban className="w-4 h-4 text-amber-300" />}
                  {batch.status === "running" && <Loader2 className="w-4 h-4 animate-spin text-sky-300" />}
                  {batch.status === "queued" && <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />}
                  <span className="capitalize">{batch.status}</span>
                  <span className="text-zinc-500">·</span>
                  <span>
                    {batch.done} / {batch.total} processed
                  </span>
                </div>
                <div className="mt-2 h-2 w-full rounded-full bg-zinc-800 overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all"
                    style={{ width: `${batch.total ? (batch.done / batch.total) * 100 : 0}%` }}
                  />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                  <div className="rounded-lg border border-zinc-900 py-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Sent</div>
                    <div className="text-emerald-300 font-display font-black text-xl" data-testid="reactivation-sent-count">
                      {batch.sent_count || 0}
                    </div>
                  </div>
                  <div className="rounded-lg border border-zinc-900 py-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Failed</div>
                    <div className="text-rose-300 font-display font-black text-xl" data-testid="reactivation-failed-count">
                      {batch.failed_count || 0}
                    </div>
                  </div>
                  <div className="rounded-lg border border-zinc-900 py-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Opted out</div>
                    <div className="text-amber-300 font-display font-black text-xl" data-testid="reactivation-optout-count">
                      {(batch.opted_out || []).length}
                    </div>
                  </div>
                </div>
              </div>

              {batch.rendered_preview && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Rendered message</div>
                  <div className="rounded-lg bg-emerald-950/10 border border-emerald-500/10 p-3 text-sm text-emerald-100 whitespace-pre-wrap">
                    {batch.rendered_preview}
                  </div>
                </div>
              )}

              {(batch.failed || []).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Failures</div>
                  <div className="rounded-lg border border-zinc-900 max-h-40 overflow-y-auto divide-y divide-zinc-900 text-xs">
                    {(batch.failed || []).map((f, i) => (
                      <div key={i} className="px-3 py-1.5 flex items-center justify-between">
                        <span className="text-zinc-400 truncate">{f.ghl_contact_id}</span>
                        <span className="text-rose-400 truncate ml-3">{f.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-zinc-900 flex items-center justify-between gap-3">
          <div className="text-[11px] text-zinc-500">
            {step === "review" &&
              `Sends pace at 1 msg / ${(preview?.pace_seconds || 3)}s. Auto-excludes no phone, opt-outs, and anyone messaged in the last ${preview?.recent_window_days || 14} days.`}
            {step === "running" && "Keep this window open — cancelling in-flight is cooperative."}
            {step === "done" && "Campaign finished. Recipients will show up in the timeline as they reply."}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {step === "review" && (
              <>
                <button
                  onClick={onClose}
                  className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-200"
                  data-testid="reactivation-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={startBatch}
                  disabled={starting || !connId || selectedIds.length === 0}
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40"
                  data-testid="reactivation-start-btn"
                >
                  {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Start sending to {selectedIds.length}
                </button>
              </>
            )}
            {step === "running" && (
              <button
                onClick={cancelBatch}
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                data-testid="reactivation-abort-btn"
              >
                <Ban className="w-3.5 h-3.5" />
                Stop sending
              </button>
            )}
            {step === "done" && (
              <button
                onClick={onClose}
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                data-testid="reactivation-done-close-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
